import { AxiosInstance, AxiosResponse } from 'axios';
import { genericFetch } from './api';
import { Auth } from '../state/auth/types';
import { get, post } from '../hooks/useAxiosPrivate';
import { ArtycSnapshotMode, SegmentEntity } from '../state/segments/types';
import { SegmentSnapshotEntity } from '../state/segmentSnapshots/types';

const ARTYC_SEGMENT_URL = '/artyc/segments';

export type JourneySummaryOutcome = 'nominal' | 'attention' | 'problem';

export interface JourneySummaryLines {
  overview: string;
  lid: string;
  faults: string;
  tracking: string;
}

export interface JourneySummaryCheckFinding {
  rule: string;
  field: string;
  detail: string;
}

export interface JourneySummaryChecks {
  version: number;
  withheld: boolean;
  hard: JourneySummaryCheckFinding[];
  soft: string[];
  attempts: number;
}

export interface JourneySummary {
  _id: string;
  outcome: JourneySummaryOutcome;
  summary: string;
  // absent on a diagnostic stored under prompt <=3.3.0
  lines?: JourneySummaryLines;
  highlights: string[];
  // Artyc-internal technical analysis, engineering audience only; absent on a
  // diagnostic stored under prompt <=4.1.0
  engineeringAnalysis?: string;
  // the numbers the summary was written from; shape follows the server digest
  digest?: Record<string, unknown>;
  model: string;
  promptVersion: string;
  createdAt: string;
  // true when the summary no longer matches the journey: an older digest or
  // prompt version, or journey data that changed after it was written. Set by
  // getJourneySummary from the server's sibling flags, not stored on the
  // diagnostic itself.
  isStale?: boolean;
  staleReasons?: string[];
  // true while the journey is neither COMPLETE nor AUTO_ENDED
  provisional?: boolean;
  // Artyc views only; absent on a diagnostic stored before the output gate
  checks?: JourneySummaryChecks;
  isWithheld?: boolean;
}

// The company route's answer: a summary while it is current, or while it is
// stale but no sweep will regenerate it any more, unless it broke a hard rule
// ('withheld'); otherwise why there is none to show.
export type JourneySummaryState =
  | 'current'
  | 'awaiting-completion'
  | 'updating'
  | 'withheld'
  | 'none';

export interface CompanyJourneySummary {
  summary: JourneySummary | null;
  state: JourneySummaryState;
}

export interface MultiJourneySummaryTheme {
  title: string;
  detail: string;
  journeyPids: string[];
}

// One summary written across a selection of journeys. `rollup` is the
// deterministic set-level view the text was written from; the report prints it
// so the numbers come from the measurement layer, not the model's prose.
export interface MultiJourneySummary {
  _id: string;
  outcome: JourneySummaryOutcome;
  headline: string;
  summary: string;
  themes: MultiJourneySummaryTheme[];
  watchlist: string[];
  rollup?: MultiJourneyRollup;
  model: string;
  promptVersion: string;
  createdAt: string;
  // absent on a summary stored before the output gate
  checks?: JourneySummaryChecks;
}

export type MktWindowSource = 'precoolEndTime' | 'cooldownWindow' | 'none';

export interface SegmentThermal {
  mktC: number | null;
  // where the MKT window opened, and which convention opened it, so a reader
  // can check the number rather than trust it
  mktWindowStartSec: number | null;
  mktWindowSource: MktWindowSource;
  // share of that window actually backed by readings at normal cadence
  mktCoveragePct: number | null;
}

export interface MultiJourneyRollupJourney {
  pid: string;
  serialNumber: string | null;
  deviceType: string | null;
  companyName: string | null;
  shippingProfileName: string | null;
  outcome: JourneySummaryOutcome;
  durationSec: number | null;
  mktC: number | null;
  timeInRangePct: number | null;
  excursionCount: number;
  longestExcursionSec: number | null;
  lidOpenCount: number;
  hardwareFaultCount: number;
  longestReportingGapSec: number | null;
}

export interface MultiJourneyRollupSpread {
  min: number;
  max: number;
  mean: number;
}

export interface MultiJourneyRollup {
  journeyCount: number;
  outcomeCounts: Record<JourneySummaryOutcome, number>;
  worstOutcome: JourneySummaryOutcome;
  companies: string[];
  deviceTypes: Record<string, number>;
  shippingProfiles: Record<string, number>;
  journeysWithExcursion: number;
  totalExcursionCount: number;
  longestExcursionSec: number | null;
  journeysWithLidOpen: number;
  journeysWithFaults: number;
  mktC: MultiJourneyRollupSpread | null;
  timeInRangePct: MultiJourneyRollupSpread | null;
  journeys: MultiJourneyRollupJourney[];
}
const companySegmentsUrl = (companyId: string | undefined) =>
  `/api/v1/company/${companyId}/segments`;

interface GetSegmentMetricResponse {
  metric: string;
}

// The export endpoints report what the response actually contains, which can be
// fewer shipments than were asked for (deleted, out of scope, or unreadable).
// Without these the UI can only echo back the size of the user's own selection
// and claims a complete download that never happened (SOF-2059).
export const EXPORTED_COUNT_HEADER = 'x-exported-segment-count';
export const REQUESTED_COUNT_HEADER = 'x-requested-segment-count';

export interface CsvDownload {
  blob: Blob;
  // undefined when the header is missing — an older server, or a proxy that
  // stripped it. Callers fall back to the requested count in that case.
  exportedCount?: number;
  requestedCount?: number;
}

const parseCount = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return typeof value === 'string' && value !== '' && Number.isFinite(parsed)
    ? parsed
    : undefined;
};

const toCsvDownload = (response: AxiosResponse<any>): CsvDownload => ({
  blob: response.data,
  exportedCount: parseCount(response.headers?.[EXPORTED_COUNT_HEADER]),
  requestedCount: parseCount(response.headers?.[REQUESTED_COUNT_HEADER]),
});
const SegmentsApi = {
  fetchMetric: async (
    axios: AxiosInstance,
    auth: Auth | null,
    endpoint: string,
    rangeStartDate: string | undefined,
    rangeEndDate: string | undefined,
    companyIdsFilter: string[] | undefined,
    forCompanyId: string | undefined
  ): Promise<string> => {
    const response: GetSegmentMetricResponse = await genericFetch(
      axios,
      auth,
      `${ARTYC_SEGMENT_URL}/metrics/${endpoint}`,
      `${companySegmentsUrl(forCompanyId)}/metrics/${endpoint}`,
      {
        rangeStartDate,
        rangeEndDate,
        companyIds: companyIdsFilter,
      }
    );
    return response.metric;
  },

  getHoursWithoutExcursion: async (
    axios: AxiosInstance,
    auth: Auth | null,
    rangeStartDate: string | undefined,
    rangeEndDate: string | undefined,
    companyIdsFilter: string[] | undefined,
    forCompanyId: string | undefined
  ): Promise<string> => {
    return SegmentsApi.fetchMetric(
      axios,
      auth,
      'hours-without-excursion',
      rangeStartDate,
      rangeEndDate,
      companyIdsFilter,
      forCompanyId
    );
  },

  getMaxDeviceUtilization: async (
    axios: AxiosInstance,
    auth: Auth | null,
    rangeStartDate: string | undefined,
    rangeEndDate: string | undefined,
    companyIdsFilter: string[] | undefined,
    forCompanyId: string | undefined
  ): Promise<string> => {
    return SegmentsApi.fetchMetric(
      axios,
      auth,
      'max-device-utilization',
      rangeStartDate,
      rangeEndDate,
      companyIdsFilter,
      forCompanyId
    );
  },

  getTotalSegments: async (
    axios: AxiosInstance,
    auth: Auth | null,
    rangeStartDate: string | undefined,
    rangeEndDate: string | undefined,
    companyIdsFilter: string[] | undefined,
    forCompanyId: string | undefined
  ): Promise<string> => {
    return SegmentsApi.fetchMetric(
      axios,
      auth,
      'total-segments',
      rangeStartDate,
      rangeEndDate,
      companyIdsFilter,
      forCompanyId
    );
  },
  downloadSegmentCsv: async (
    axios: AxiosInstance,
    isArtyc: boolean,
    companyId: string | undefined,
    segmentIds: string[]
  ): Promise<CsvDownload> => {
    const artycUrl = `${ARTYC_SEGMENT_URL}/csvs`;
    const companyUrl = `${companySegmentsUrl(companyId)}/csvs`;
    const entityURL = isArtyc ? artycUrl : companyUrl;

    // TODO: type
    const response: AxiosResponse<any> = await post(
      axios,
      entityURL,
      { segmentIds },
      { responseType: 'blob' }
    );

    return toCsvDownload(response);
  },
  downloadLogCsv: async (
    axios: AxiosInstance,
    isArtyc: boolean,
    segmentIds: string[]
  ): Promise<CsvDownload> => {
    if (!isArtyc) {
      throw new Error('expecting artyc user');
    }

    const artycUrl = `${ARTYC_SEGMENT_URL}/logs`;
    // can't be used by company atm
    // const companyUrl = `${COMPANY_SEGMENT_URL(auth)}/logs`;
    const entityURL = artycUrl;

    // TODO: type
    const response: AxiosResponse<any> = await post(
      axios,
      entityURL,
      { segmentIds },
      { responseType: 'blob' }
    );

    return toCsvDownload(response);
  },

  getSegmentSnapshots: async (
    axios: AxiosInstance,
    segmentId: string,
    isArtyc: boolean,
    companyId: string | undefined,
    artycSnapshotsMode?: ArtycSnapshotMode,
    // when set, the server downsamples to ~maxPoints (keeping event snapshots)
    // so long journeys don't ship the whole series to the browser
    maxPoints?: number
  ) => {
    const artycUrl = `${ARTYC_SEGMENT_URL}/${segmentId}/snapshots`;
    const companyUrl = `${companySegmentsUrl(
      companyId
    )}/${segmentId}/snapshots`;
    const entityURL = isArtyc ? artycUrl : companyUrl;
    const response: AxiosResponse<SegmentSnapshotEntity[]> = await get(
      axios,
      entityURL,
      { mode: artycSnapshotsMode, maxPoints }
    );

    return response.data;
  },
  getSegmentSnapshotsCount: async (
    axios: AxiosInstance,
    segmentId: string,
    isArtyc: boolean,
    companyId: string | undefined,
    artycSnapshotsMode?: ArtycSnapshotMode
  ): Promise<number> => {
    const artycUrl = `${ARTYC_SEGMENT_URL}/${segmentId}/snapshots/count`;
    const companyUrl = `${companySegmentsUrl(
      companyId
    )}/${segmentId}/snapshots/count`;
    const entityURL = isArtyc ? artycUrl : companyUrl;
    const response: AxiosResponse<{ total: number }> = await get(
      axios,
      entityURL,
      { mode: artycSnapshotsMode }
    );
    return response.data.total;
  },
  // The server's mean kinetic temperature. Computed there over the whole
  // stored stream so the card, the PDF and the AI report all show one number;
  // the snapshots the browser holds are downsampled and would not agree.
  getSegmentThermal: async (
    axios: AxiosInstance,
    segmentId: string,
    isArtyc: boolean,
    companyId: string | undefined
  ): Promise<SegmentThermal> => {
    const artycUrl = `${ARTYC_SEGMENT_URL}/${segmentId}/thermal`;
    const companyUrl = `${companySegmentsUrl(companyId)}/${segmentId}/thermal`;
    const response: AxiosResponse<SegmentThermal> = await get(
      axios,
      isArtyc ? artycUrl : companyUrl
    );
    return response.data;
  },
  // Generation stays internal (it spends a model call), but reading is opened
  // per company via the JOURNEY_INSIGHTS flag. The company route serves the
  // customer view: no engineering analysis, no 5L-I maintenance group.
  getJourneySummary: async (
    axios: AxiosInstance,
    segmentId: string,
    isArtyc = true,
    companyId?: string
  ): Promise<JourneySummary | null> => {
    const response: AxiosResponse<{
      diagnostic: JourneySummary | null;
      isStale?: boolean;
      staleReasons?: string[];
      provisional?: boolean;
      isWithheld?: boolean;
    }> = await get(
      axios,
      isArtyc
        ? `${ARTYC_SEGMENT_URL}/${segmentId}/diagnostic`
        : `${companySegmentsUrl(companyId)}/${segmentId}/diagnostic`
    );
    const { diagnostic, isStale, staleReasons, provisional, isWithheld } =
      response.data;
    return diagnostic === null
      ? null
      : { ...diagnostic, isStale, staleReasons, provisional, isWithheld };
  },
  getJourneySummaryState: async (
    axios: AxiosInstance,
    segmentId: string,
    companyId: string | undefined
  ): Promise<CompanyJourneySummary> => {
    const response: AxiosResponse<{
      diagnostic: JourneySummary | null;
      state?: JourneySummaryState;
    }> = await get(
      axios,
      `${companySegmentsUrl(companyId)}/${segmentId}/diagnostic`
    );
    const { diagnostic, state } = response.data;
    return {
      summary: diagnostic,
      state: state ?? (diagnostic === null ? 'none' : 'current'),
    };
  },
  generateJourneySummary: async (
    axios: AxiosInstance,
    segmentId: string
  ): Promise<JourneySummary> => {
    const response: AxiosResponse<{ diagnostic: JourneySummary }> = await post(
      axios,
      `${ARTYC_SEGMENT_URL}/${segmentId}/diagnostic`,
      {}
    );
    const { diagnostic } = response.data;
    return { ...diagnostic, isWithheld: diagnostic.checks?.withheld === true };
  },
  // One model call over every already-analysed journey in the selection. The
  // server skips segments with no stored diagnostic, so callers analyse first.
  generateMultiJourneySummary: async (
    axios: AxiosInstance,
    segmentIds: string[]
  ): Promise<MultiJourneySummary> => {
    const response: AxiosResponse<{ summary: MultiJourneySummary }> =
      await post(axios, `${ARTYC_SEGMENT_URL}/diagnostics/summary`, {
        segmentIds,
      });
    return response.data.summary;
  },
  getSegment: async (
    axios: AxiosInstance,
    auth: Auth | null,
    segmentId: string
  ): Promise<SegmentEntity> => {
    const response: SegmentEntity = await genericFetch(
      axios,
      auth,
      `${ARTYC_SEGMENT_URL}/${segmentId}`,
      `${companySegmentsUrl(auth?.companyId)}/${segmentId}`
    );
    return response;
  },
};

export default SegmentsApi;
